import { useState } from 'react';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useOutstandingCheckouts, useCheckinTool, type EnrichedCheckout } from './hooks/useCustody.js';

export interface ToolCustodyPanelProps {
  readonly shopId: string | null;
}

/** Hours elapsed since checkout was created */
function hoursElapsed(checkedOutAt: string): number {
  return (Date.now() - new Date(checkedOutAt).getTime()) / 3_600_000;
}

function formatElapsed(hours: number): string {
  if (hours < 1) {
    const mins = Math.round(hours * 60);
    return `${mins}m`;
  }
  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }
  return `${Math.floor(hours / 24)}d ${Math.round(hours % 24)}h`;
}

function elapsedColor(hours: number): string {
  if (hours < 1) return 'var(--color-green)';
  if (hours < 4) return 'var(--color-yellow)';
  return 'var(--color-red)';
}

function slotLabel(n: number): string {
  return `T${String(n).padStart(2, '0')}`;
}

interface CheckinModalProps {
  readonly checkout: EnrichedCheckout;
  readonly onClose: () => void;
  readonly shopId: string;
}

function CheckinModal({ checkout, onClose, shopId }: CheckinModalProps): JSX.Element {
  const [geometryChanged, setGeometryChanged] = useState(false);
  const [newLength, setNewLength] = useState('');
  const checkin = useCheckinTool(shopId);

  async function handleConfirm() {
    await checkin.mutateAsync({
      holder_id:        checkout.holder_id,
      geometry_changed: geometryChanged,
      new_length_mm:    geometryChanged && newLength ? parseFloat(newLength) : null,
      notes:            null,
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-80 rounded-md border border-border bg-card p-5 shadow-xl">
        <h3 className="font-mono text-sm mb-1">Check In Tool</h3>
        <p className="text-xs text-text-dim mb-4">
          {checkout.holder_description ?? checkout.holder_id} &mdash;&nbsp;
          {checkout.from_machine_id} {slotLabel(checkout.from_slot)}
        </p>

        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={geometryChanged}
            onChange={(e) => setGeometryChanged(e.target.checked)}
            className="w-4 h-4 accent-accent"
          />
          <span className="text-xs">Geometry changed (re-measure required)</span>
        </label>

        {geometryChanged && (
          <div className="mb-4">
            <label className="block text-[10px] font-mono text-text-dim uppercase tracking-wider mb-1">
              New Length (mm)
            </label>
            <input
              type="number"
              step="0.001"
              value={newLength}
              onChange={(e) => setNewLength(e.target.value)}
              placeholder="e.g. 75.432"
              className="w-full rounded border border-border bg-surface px-2 py-1.5 text-xs font-mono placeholder:text-text-dim focus:outline-none focus:border-accent"
            />
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-border hover:bg-surface transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { void handleConfirm(); }}
            disabled={checkin.isPending || (geometryChanged && !newLength)}
            className="px-3 py-1.5 text-xs rounded bg-accent text-black font-mono hover:brightness-110 disabled:opacity-50 transition-all"
          >
            {checkin.isPending ? 'Checking in…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ToolCustodyPanel({ shopId }: ToolCustodyPanelProps): JSX.Element {
  const { data: checkouts, isLoading, isError } = useOutstandingCheckouts(shopId);
  const [checkinTarget, setCheckinTarget] = useState<EnrichedCheckout | null>(null);

  const sorted = checkouts
    ? [...checkouts].sort(
        (a, b) => new Date(a.checked_out_at).getTime() - new Date(b.checked_out_at).getTime(),
      )
    : [];

  const overdueCount = sorted.filter((c) => hoursElapsed(c.checked_out_at) >= 4).length;

  return (
    <section className="rounded-md border border-border bg-card overflow-hidden">
      <header className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
          Tool Custody
        </h2>
        {overdueCount > 0 && (
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded"
            style={{ backgroundColor: 'var(--color-red)', color: '#000' }}
          >
            {overdueCount} overdue
          </span>
        )}
      </header>

      {isLoading && (
        <div className="px-4 py-3 space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      )}

      {!isLoading && isError && (
        <p className="px-4 py-6 text-center text-xs text-text-dim font-mono">
          Failed to load custody data.
        </p>
      )}

      {!isLoading && !isError && sorted.length === 0 && (
        <p className="px-4 py-6 text-center text-xs text-text-dim font-mono">
          No tools currently checked out.
        </p>
      )}

      {!isLoading && !isError && sorted.length > 0 && (
        <ul>
          {sorted.map((c) => {
            const hours = hoursElapsed(c.checked_out_at);
            const color = elapsedColor(hours);
            return (
              <li
                key={c.checkout_id}
                className="flex items-center justify-between px-4 py-2.5 border-t border-border first:border-0"
              >
                <div className="min-w-0 mr-3">
                  <p className="font-mono text-xs truncate">
                    {c.holder_description ?? c.holder_id}
                  </p>
                  <p className="text-[10px] text-text-dim">
                    {c.from_machine_id} {slotLabel(c.from_slot)} &middot; {c.checked_out_by}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="font-mono text-xs"
                    style={{ color }}
                  >
                    {formatElapsed(hours)}
                  </span>
                  <button
                    onClick={() => setCheckinTarget(c)}
                    className="text-[10px] px-2 py-1 rounded border border-border hover:bg-surface font-mono transition-colors"
                  >
                    Check In
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="px-4 py-2 text-[10px] font-mono text-muted border-t border-border">
        {sorted.length === 0
          ? 'All tools accounted for'
          : `${sorted.length} tool${sorted.length !== 1 ? 's' : ''} out &mdash; green &lt;1h · yellow 1–4h · red &gt;4h`}
      </p>

      {checkinTarget && shopId && (
        <CheckinModal
          checkout={checkinTarget}
          shopId={shopId}
          onClose={() => setCheckinTarget(null)}
        />
      )}
    </section>
  );
}
